import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { Chip } from '@/components/Chip'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Field, Select, TextArea, TextInput } from '@/components/Field'
import { BottomSheet } from '@/components/BottomSheet'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import type { EarlyCareState, Person, PersonType } from '@/lib/types'
import { PACK_COLORS } from '@/design-system/tokens'
import { ageString, absoluteDate } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { HealthIcon } from '@/design-system/icons'
import { Celebration } from '@/components/Celebration'
import { uploadPersonMedia, clearPersonMedia, signedAttachmentUrl } from '@/lib/attachments'
import { WhoChart, type GrowthPoint } from '@/components/WhoChart'
import { GrowthVelocity, type WeightPoint } from '@/components/GrowthVelocity'
import { VaccinesSection } from '@/components/VaccinesSection'
import { PediatricianSection } from '@/components/PediatricianSection'
import { EmergencyContactsSection } from '@/components/EmergencyContactsSection'
import { DevMilestonesSection } from '@/components/DevMilestonesSection'
import { CelebrationSignaturePicker } from '@/components/CelebrationSignaturePicker'

export function PersonProfileScreen() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const refresh = useAppStore((s) => s.refreshFamilyContext)
  const [editing, setEditing] = useState(false)
  const [ceremonyOpen, setCeremonyOpen] = useState(false)
  const [celebration, setCelebration] = useState<{ name: string; dob: string } | null>(null)
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)

  const personQ = useQuery({
    enabled: !!id,
    queryKey: ['person', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('persons').select('*').eq('id', id!).maybeSingle()
      if (error) throw error
      if (!data) throw new Error('Not found')
      return data as Person
    },
  })

  const stateQ = useQuery({
    enabled: !!id,
    queryKey: ['ecs', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('early_care_state')
        .select('*')
        .eq('person_id', id!)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as EarlyCareState | null
    },
  })

  const growthQ = useQuery({
    enabled: !!id,
    queryKey: ['person-growth', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('measurements')
        .select('id, measurement_type, value, unit, measured_at')
        .eq('person_id', id!)
        .order('measured_at', { ascending: false })
        .limit(20)
      if (error) throw error
      return (data ?? []) as Array<{
        id: string
        measurement_type: string
        value: number
        unit: string
        measured_at: string
      }>
    },
  })

  const needsQ = useQuery({
    enabled: !!id,
    queryKey: ['person-needs', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('needs')
        .select('id, title, urgency, status')
        .eq('person_id', id!)
        .in('status', ['open', 'in_progress'])
        .order('urgency', { ascending: true })
        .limit(30)
      if (error) throw error
      return data ?? []
    },
  })

  if (personQ.isLoading) return <Screen title="Profile" back><Loading /></Screen>
  if (personQ.isError) return <Screen title="Profile" back><ErrorPanel message={(personQ.error as Error).message} /></Screen>

  const person = personQ.data!
  const ecs = stateQ.data ?? null
  const packColor = PACK_COLORS.early_care.light

  // No useMemo here — must not add hooks after the early returns above (Rules of Hooks).
  // Tiny switch, no perf concern.
  const packBadges: string[] = (() => {
    switch (person.person_type) {
      case 'unborn': return ['Pregnancy']
      case 'baby': return ['Early Care', 'Health']
      case 'child': return ['Health', 'Activity', 'School']
      case 'adult': return ['—']
    }
  })()

  return (
    <Screen title="Profile" back action={<button onClick={() => setEditing(true)} className="text-caption text-text-secondary">Edit</button>}>
      <PersonHero person={person} onUpdated={async () => { await personQ.refetch() }} />

      <div className="px-5 pt-5 space-y-5">
        <div className="flex flex-wrap gap-2">
          <Chip color={packColor}>Identity · {person.identity_status.replace(/_/g, ' ')}</Chip>
          {packBadges.map((b) => (
            <Chip key={b}>{b}</Chip>
          ))}
        </div>

        {person.person_type === 'unborn' && (
          <section
            className="rounded-r-sm p-5"
            style={{ background: `linear-gradient(180deg, ${PACK_COLORS.pregnancy.light}18, transparent), #141B19`, borderTop: `1px solid ${PACK_COLORS.pregnancy.light}55` }}
          >
            <Kicker color={PACK_COLORS.pregnancy.light}>Baby has arrived?</Kicker>
            <p className="mt-2 text-body text-text-secondary">
              When {person.display_name} is born, tap the button to record the birth. This transitions the record from pregnancy to
              early care, opens up feeding + diaper + sleep tracking, and preserves the pregnancy history.
            </p>
            <div className="mt-4">
              <Button variant="primary" size="lg" packColor={PACK_COLORS.pregnancy.light} onClick={() => setCeremonyOpen(true)} className="w-full">
                Baby is here 🌱
              </Button>
            </div>
          </section>
        )}

        {ecs && person.person_type !== 'adult' && person.person_type !== 'unborn' && (
          <section>
            <Kicker>Today at a glance</Kicker>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Stat label="Feeds today" value={ecs.feed_count_24h.toString()} />
              <Stat label="Volume today" value={`${ecs.feed_volume_24h_ml} mL`} />
              <Stat label="Wet diapers" value={ecs.wet_count_24h.toString()} />
              <Stat label="Dirty diapers" value={ecs.dirty_count_24h.toString()} />
            </div>
          </section>
        )}

        {person.person_type !== 'unborn' && (
          <section>
            <div className="flex items-center justify-between">
              <Kicker color={PACK_COLORS.health.light}>Growth</Kicker>
              <button
                type="button"
                onClick={() => nav('/log/growth')}
                className="text-caption text-text-secondary underline underline-offset-2 hover:text-text-primary"
              >
                + Log
              </button>
            </div>

            {growthQ.isLoading && <div className="mt-2 p-4 text-caption text-text-tertiary">Loading…</div>}
            {growthQ.data?.length === 0 && (
              <div className="mt-2 glass-tile p-4 text-body text-text-tertiary flex items-center gap-3">
                <HealthIcon size={20} className="text-text-quiet" />
                <div className="flex-1">
                  <div className="text-text-secondary">No measurements yet.</div>
                  <div className="text-caption text-text-quiet mt-0.5">Record weight, height, head circumference after pediatric visits.</div>
                </div>
              </div>
            )}

            {growthQ.data && growthQ.data.length > 0 && person.date_of_birth && (() => {
              const dob = new Date(person.date_of_birth)
              const weightPoints: WeightPoint[] = growthQ.data
                .filter((r) => r.measurement_type === 'weight' && r.unit === 'kg')
                .map((r) => ({ at: r.measured_at, kg: r.value, ageMonths: monthsBetween(dob, new Date(r.measured_at)) }))
                .sort((a, b) => a.ageMonths - b.ageMonths)
              return (
                <div className="mt-3">
                  {weightPoints.length >= 2 && <GrowthVelocity points={weightPoints} />}
                </div>
              )
            })()}

            {growthQ.data && growthQ.data.length > 0 && person.date_of_birth && (
              <>
                {/* WHO chart: weight-for-age (has LMS bands), height + head fallback to raw-only */}
                {(['weight', 'height', 'head_circumference'] as const).map((kind) => {
                  const rows = growthQ.data!.filter((m) => m.measurement_type === kind)
                  if (rows.length === 0) return null
                  const dob = new Date(person.date_of_birth!)
                  const points: GrowthPoint[] = rows.map((r) => ({
                    ageMonths: monthsBetween(dob, new Date(r.measured_at)),
                    value: r.value,
                    at: r.measured_at,
                  })).sort((a, b) => a.ageMonths - b.ageMonths)
                  return (
                    <div key={kind} className="mt-3">
                      <WhoChart kind={kind} sex={person.sex} points={points} />
                    </div>
                  )
                })}

                {/* Compact list below the charts */}
                <div className="mt-3 rounded-r-sm bg-bg-elevated border border-divider-subtle">
                  <div className="divide-y divide-divider-subtle">
                    {growthQ.data.slice(0, 8).map((m) => (
                      <div key={m.id} className="px-4 py-2.5 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-body text-text-primary">
                            {prettyMeasurementType(m.measurement_type)} · {m.value} {m.unit}
                          </div>
                          <div className="text-caption text-text-tertiary mt-0.5">{absoluteDate(m.measured_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {person.date_of_birth && person.person_type !== 'unborn' && person.person_type !== 'adult' && (
          <>
            <DevMilestonesSection person={person} />
            <VaccinesSection person={person} />
          </>
        )}

        {person.person_type !== 'unborn' && person.person_type !== 'adult' && (
          <>
            <PediatricianSection person={person} />
            <EmergencyContactsSection person={person} />
          </>
        )}

        <CelebrationSignaturePicker personId={person.id} personName={person.display_name} />

        <section>
          <Kicker>Open needs</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle">
            {needsQ.data?.length === 0 && <div className="p-4 text-body text-text-tertiary">None open.</div>}
            {needsQ.data?.map((n) => (
              <button
                key={n.id}
                onClick={() => nav(`/needs/${n.id}`)}
                type="button"
                className="block w-full text-left px-4 py-3 hover:bg-white/5 border-b border-divider-subtle last:border-0"
              >
                <div className="text-body text-text-primary truncate">{n.title}</div>
                <div className="text-caption text-text-tertiary mt-0.5">{n.urgency} · {n.status}</div>
              </button>
            ))}
          </div>
        </section>

        {person.person_type === 'child' && person.date_of_birth && (
          <section>
            <Kicker>Identity governance</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4">
              <div className="text-caption text-text-tertiary">Current control</div>
              <div className="text-body text-text-primary mt-0.5">{person.identity_status.replace(/_/g, ' ')}</div>
              <div className="text-caption text-text-tertiary mt-3">
                When {person.display_name} turns 18, they can link their own account to take control of this record.
                Parents lose write access; the record stays intact.
              </div>
            </div>
          </section>
        )}

        {person.identity_status !== 'archived' && (
          <section>
            <Kicker color={PACK_COLORS.safety.light}>Danger zone</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 space-y-3">
              <div className="text-caption text-text-secondary">
                Archiving hides {person.display_name} from the family view. The record stays intact — every feed, diaper, milestone, and measurement is preserved. Archive is reversible from the database, but not yet from the app.
              </div>
              <Button
                variant="destructive"
                size="md"
                onClick={async () => {
                  const confirmed = window.confirm(
                    `Archive ${person.display_name}? They'll be hidden from the family view but no data is deleted.`,
                  )
                  if (!confirmed) return
                  try {
                    const { error } = await supabase.from('persons').update({ identity_status: 'archived' }).eq('id', person.id)
                    if (error) throw new Error(error.message)
                    if (familyId && user) {
                      await emitEvent({
                        familyId,
                        personId: person.id,
                        userId: user.id,
                        eventType: 'person.archived',
                        payload: {
                          display_name: person.display_name,
                          previous_status: person.identity_status,
                        } as import('@/lib/types').JSONValue,
                      })
                    }
                    await personQ.refetch()
                    await refresh()
                    nav('/family', { replace: true })
                  } catch (e) {
                    window.alert(`Archive failed: ${e instanceof Error ? e.message : String(e)}`)
                  }
                }}
              >
                Archive {person.display_name}
              </Button>
            </div>
          </section>
        )}
      </div>

      <EditPersonSheet
        open={editing}
        onClose={() => setEditing(false)}
        person={person}
        onSaved={async () => {
          await personQ.refetch()
          await refresh()
        }}
      />
      <BirthCeremonySheet
        open={ceremonyOpen}
        onClose={() => setCeremonyOpen(false)}
        person={person}
        onCompleted={async (info) => {
          await personQ.refetch()
          await refresh()
          if (info) setCelebration(info)
        }}
      />
      <Celebration
        open={!!celebration}
        title={celebration ? `${celebration.name} is here.` : ''}
        subtitle={celebration ? `Welcome to the world · ${new Date(celebration.dob).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}
        palette="pregnancy"
        onDismiss={() => setCelebration(null)}
      />
    </Screen>
  )
}

/**
 * Facebook-style profile hero: cover photo (or pack-color gradient fallback)
 * with circular avatar (or initials fallback) overlapping the bottom-left.
 * Each surface has a subtle camera icon in the corner — tap to upload/change.
 *
 * Signed URLs are 1hr TTL. On mount the hero fetches fresh signed URLs for
 * whichever refs the person has. Old signed URLs held by React Query are
 * stale after 1hr but re-fetch happens on next mount.
 */
function PersonHero({ person, onUpdated }: { person: Person; onUpdated: () => void | Promise<void> }) {
  const familyId = useAppStore((s) => s.familyId)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState<'avatar' | 'cover' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const avatarInput = useRef<HTMLInputElement>(null)
  const coverInput = useRef<HTMLInputElement>(null)

  // Palette per person type — used as gradient fallback for cover and as
  // avatar ring accent.
  const palette = person.person_type === 'unborn'
    ? PACK_COLORS.pregnancy.light
    : person.person_type === 'baby' || person.person_type === 'child'
      ? PACK_COLORS.early_care.light
      : PACK_COLORS.health.light

  // Fetch signed URLs on mount + whenever refs change (after upload).
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (person.avatar_storage_ref) {
        const url = await signedAttachmentUrl(person.avatar_storage_ref, 3600)
        if (!cancelled) setAvatarUrl(url)
      } else {
        setAvatarUrl(null)
      }
      if (person.cover_storage_ref) {
        const url = await signedAttachmentUrl(person.cover_storage_ref, 3600)
        if (!cancelled) setCoverUrl(url)
      } else {
        setCoverUrl(null)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [person.avatar_storage_ref, person.cover_storage_ref])

  async function handleUpload(kind: 'avatar' | 'cover', file: File | undefined) {
    if (!file || !familyId) return
    setError(null)
    // Client-side sanity: max 6 MB, must be image/*
    if (!file.type.startsWith('image/')) {
      setError('Only image files (JPG, PNG, HEIC, WebP).')
      return
    }
    if (file.size > 6 * 1024 * 1024) {
      setError('Image is over 6 MB — try compressing first.')
      return
    }
    setUploading(kind)
    try {
      await uploadPersonMedia({ familyId, personId: person.id, kind, file })
      await onUpdated()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setUploading(null)
    }
  }

  async function handleClear(kind: 'avatar' | 'cover') {
    setError(null)
    setUploading(kind)
    try {
      await clearPersonMedia({ personId: person.id, kind })
      await onUpdated()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setUploading(null)
    }
  }

  const coverStyle: React.CSSProperties = coverUrl
    ? {
        backgroundImage: `url(${coverUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        // Ambient wash — mint bloom + coral pin, ref1+ref2 style. No cover uploaded yet.
        background: `
          radial-gradient(80% 60% at 30% 40%, rgba(46,210,163,0.22), transparent 60%),
          radial-gradient(50% 40% at 80% 80%, rgba(255,122,45,0.18), transparent 60%),
          linear-gradient(135deg, ${palette}22, ${palette}05 60%, transparent)
        `,
      }

  return (
    <div className="relative">
      {/* Cover hero — edge-to-edge, 240px tall, taller than v1 for cinematic feel */}
      <div className="relative h-[240px] w-full overflow-hidden" style={coverStyle}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bg-base/80" />
        <button
          type="button"
          aria-label={coverUrl ? 'Change cover photo' : 'Add cover photo'}
          onClick={() => coverInput.current?.click()}
          disabled={uploading === 'cover'}
          className="absolute top-3 right-3 inline-flex h-9 items-center gap-1.5 rounded-full bg-black/50 px-3 text-caption text-white backdrop-blur-md hover:bg-black/70 disabled:opacity-50"
        >
          {uploading === 'cover' ? 'Uploading…' : coverUrl ? '✎ Change cover' : '＋ Cover photo'}
        </button>
        {coverUrl && uploading !== 'cover' && (
          <button
            type="button"
            aria-label="Remove cover photo"
            onClick={() => handleClear('cover')}
            className="absolute top-3 right-[168px] inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md hover:bg-black/70"
          >
            ✕
          </button>
        )}
        <input
          ref={coverInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => { void handleUpload('cover', e.target.files?.[0]) }}
        />
      </div>

      {/* Avatar + name row — negative margin overlaps the cover */}
      <div className="relative -mt-14 px-5">
        <div className="flex items-end gap-4">
          <div className="relative">
            <button
              type="button"
              aria-label={avatarUrl ? 'Change profile photo' : 'Add profile photo'}
              onClick={() => avatarInput.current?.click()}
              disabled={uploading === 'avatar'}
              className="relative block rounded-full ring-4 ring-bg-base"
              style={{ boxShadow: `0 0 0 1px ${palette}55` }}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={person.display_name}
                  className="block h-24 w-24 rounded-full object-cover"
                  draggable={false}
                />
              ) : (
                <Avatar name={person.display_name} size={96} />
              )}
              <span
                className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-bg-base text-caption text-text-secondary ring-2 ring-bg-base"
                style={{ boxShadow: `0 0 0 1px ${palette}55` }}
              >
                {uploading === 'avatar' ? '⏳' : '📷'}
              </span>
            </button>
            <input
              ref={avatarInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { void handleUpload('avatar', e.target.files?.[0]) }}
            />
          </div>

          <div className="flex-1 min-w-0 pb-1">
            <div
              className="font-display text-hero text-text-primary truncate"
              style={{ fontWeight: 500, letterSpacing: '-0.02em' }}
            >
              {person.display_name}
            </div>
            <div className="text-caption text-text-tertiary mt-0.5">
              {person.person_type}
              {person.date_of_birth && ` · born ${absoluteDate(person.date_of_birth)}`}
              {person.due_date && ` · due ${absoluteDate(person.due_date)}`}
            </div>
            {person.date_of_birth && (
              <div className="text-body text-text-secondary mt-1">{ageString(person.date_of_birth)}</div>
            )}
          </div>
        </div>

        {avatarUrl && uploading !== 'avatar' && (
          <button
            type="button"
            onClick={() => handleClear('avatar')}
            className="mt-3 text-caption text-text-tertiary underline underline-offset-2 hover:text-text-primary"
          >
            Remove profile photo
          </button>
        )}
        {error && (
          <div className="mt-3 rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

function monthsBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime()
  return ms / (1000 * 60 * 60 * 24 * 30.4375)
}

function prettyMeasurementType(t: string): string {
  switch (t) {
    case 'weight': return 'Weight'
    case 'height': return 'Height'
    case 'head_circumference': return 'Head'
    case 'temperature': return 'Temp'
    default: return t.replace(/_/g, ' ')
  }
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3">
      <div className="text-caption text-text-tertiary">{label}</div>
      <div className="mt-1 font-display text-title text-text-primary">{value}</div>
    </div>
  )
}

function Loading() {
  return <div className="py-10 text-center text-text-tertiary text-caption">Loading…</div>
}
function ErrorPanel({ message }: { message: string }) {
  return <div className="p-4 rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 text-caption text-pack-safety">{message}</div>
}

function EditPersonSheet({ open, onClose, person, onSaved }: { open: boolean; onClose: () => void; person: Person; onSaved: () => void | Promise<void> }) {
  const [displayName, setDisplayName] = useState(person.display_name)
  const [personType, setPersonType] = useState<PersonType>(person.person_type)
  const [dob, setDob] = useState(person.date_of_birth ?? '')
  const [due, setDue] = useState(person.due_date ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    try {
      const patch: Record<string, unknown> = {
        display_name: displayName.trim(),
        person_type: personType,
        date_of_birth: personType === 'baby' || personType === 'child' ? dob || null : null,
        due_date: personType === 'unborn' ? due || null : null,
      }
      const { error } = await supabase.from('persons').update(patch).eq('id', person.id)
      if (error) throw new Error(error.message)
      await onSaved()
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={`Edit ${person.display_name}`}
      action={
        <Button variant="primary" size="lg" packColor={PACK_COLORS.early_care.light} loading={busy} onClick={save} className="w-full">
          Save
        </Button>
      }
    >
      <div className="space-y-4 pt-4">
        <Field label="Display name">
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Person type">
          <Select value={personType} onChange={(e) => setPersonType(e.target.value as PersonType)}>
            <option value="unborn">Unborn</option>
            <option value="baby">Baby</option>
            <option value="child">Child</option>
            <option value="adult">Adult</option>
          </Select>
        </Field>
        {(personType === 'baby' || personType === 'child') && (
          <Field label="Date of birth">
            <TextInput type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </Field>
        )}
        {personType === 'unborn' && (
          <Field label="Due date">
            <TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
        )}
        {err && <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>}
      </div>
    </BottomSheet>
  )
}

// ==================== BirthCeremonySheet ====================

interface CeremonyProps {
  open: boolean
  onClose: () => void
  person: Person
  onCompleted: (info: { name: string; dob: string }) => void | Promise<void>
}

/**
 * The unborn → baby transition. This is a one-way ceremony:
 *   1. Update persons.person_type = 'baby', persons.date_of_birth = <date>, persons.due_date = NULL
 *   2. Emit person.updated event with { from_type, to_type, dob, birth_time }
 *   3. Optionally insert measurements (weight/length/head circumference) + emit growth.recorded event per measurement
 * All writes happen via authenticated user paths — persons UPDATE and measurements INSERT both allowed for owner/parent.
 */
function BirthCeremonySheet({ open, onClose, person, onCompleted }: CeremonyProps) {
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)

  const today = new Date().toISOString().slice(0, 10)
  const [displayName, setDisplayName] = useState(
    person.display_name === 'Baby' || person.display_name === 'Little One' ? '' : person.display_name,
  )
  const [dob, setDob] = useState(today)
  const [birthTime, setBirthTime] = useState('')
  const [sex, setSex] = useState<'female' | 'male' | 'intersex' | 'unspecified' | ''>('')
  const [weightKg, setWeightKg] = useState('')
  const [lengthCm, setLengthCm] = useState('')
  const [headCm, setHeadCm] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function commit() {
    if (!familyId || !user) return
    setBusy(true); setErr(null)
    try {
      if (!displayName.trim()) throw new Error("Please enter the baby's name")
      if (!dob) throw new Error('Please enter the date of birth')

      // Compose an occurred_at from dob + optional birthTime (interpreted as local time).
      let occurredAt: Date
      if (birthTime && /^\d{2}:\d{2}$/.test(birthTime)) {
        occurredAt = new Date(`${dob}T${birthTime}:00`)
      } else {
        occurredAt = new Date(`${dob}T00:00:00`)
      }
      if (Number.isNaN(occurredAt.getTime())) throw new Error('Invalid date or time')

      const trimmedName = displayName.trim()
      const patch: Record<string, unknown> = {
        display_name: trimmedName,
        person_type: 'baby',
        date_of_birth: dob,
        due_date: null,
      }
      if (sex) patch.sex = sex

      const { error: personErr } = await supabase.from('persons').update(patch).eq('id', person.id)
      if (personErr) throw new Error(`Failed to update baby: ${personErr.message}`)

      // Emit person.updated for the transition. Records who did it and when in the events stream
      // so the projection function can pick up the life_stage change.
      await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType: 'person.updated',
        payload: {
          transition: 'unborn_to_baby',
          from_type: 'unborn',
          to_type: 'baby',
          date_of_birth: dob,
          birth_time: birthTime || null,
          sex: sex || null,
          display_name: trimmedName,
          note: note.trim() || null,
        } as import('@/lib/types').JSONValue,
        occurredAt,
      })

      // Insert measurements + emit growth.recorded per measurement.
      const measurements: Array<{ measurement_type: string; value: number; unit: string }> = []
      if (weightKg) {
        const kg = parseFloat(weightKg)
        if (!Number.isFinite(kg) || kg <= 0 || kg > 10) throw new Error('Weight should be between 0 and 10 kg')
        measurements.push({ measurement_type: 'weight', value: kg, unit: 'kg' })
      }
      if (lengthCm) {
        const cm = parseFloat(lengthCm)
        if (!Number.isFinite(cm) || cm <= 0 || cm > 100) throw new Error('Length should be between 0 and 100 cm')
        measurements.push({ measurement_type: 'height', value: cm, unit: 'cm' })
      }
      if (headCm) {
        const cm = parseFloat(headCm)
        if (!Number.isFinite(cm) || cm <= 0 || cm > 60) throw new Error('Head circumference should be between 0 and 60 cm')
        measurements.push({ measurement_type: 'head_circumference', value: cm, unit: 'cm' })
      }

      if (measurements.length > 0) {
        const rows = measurements.map((m) => ({
          family_id: familyId,
          person_id: person.id,
          measurement_type: m.measurement_type,
          value: m.value,
          unit: m.unit,
          measured_at: occurredAt.toISOString(),
          measured_by: 'parent', // CHECK constraint requires role: 'parent'|'provider'|'self'|'manual'|'import'
          source: 'ceremony',
        }))
        const { error: measErr } = await supabase.from('measurements').insert(rows)
        if (measErr) throw new Error(`Failed to record measurements: ${measErr.message}`)

        // Emit one growth.recorded event per measurement so it shows on the timeline.
        for (const m of measurements) {
          await emitEvent({
            familyId,
            personId: person.id,
            userId: user.id,
            eventType: 'growth.recorded',
            payload: {
              measurement_type: m.measurement_type,
              value: m.value,
              unit: m.unit,
              context: 'birth',
            } as import('@/lib/types').JSONValue,
            occurredAt,
          })
        }
      }

      // Re-run the projection so life_stage cascades from 'unborn' → 'newborn'.
      // Called as fire-and-forget — projection isn't user-blocking.
      try { await supabase.rpc('project_person_state', { p_person_id: person.id }) } catch { /* noop */ }

      // Lifecycle signal — birth ceremony completed.
      import('@/lib/errorReporter').then((m) => m.reportInfo('birth_ceremony_completed', {
        person_id: person.id,
        dob,
        has_measurements: measurements.length > 0,
      }))

      // Close the sheet FIRST so the celebration overlay renders over a clean parent screen.
      onClose()
      await onCompleted({ name: trimmedName, dob })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Baby has arrived"
      action={
        <Button variant="primary" size="lg" packColor={PACK_COLORS.pregnancy.light} loading={busy} onClick={commit} className="w-full">
          Save birth
        </Button>
      }
    >
      <div className="space-y-4 pt-3">
        <div className="text-caption text-text-tertiary">
          Congratulations. Recording the birth transitions {person.display_name} from pregnancy to early care and starts feed / diaper / sleep tracking. All optional fields can be filled in later from the profile.
        </div>

        <Field label="Baby's name">
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="First name" required autoFocus />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date of birth">
            <TextInput type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
          </Field>
          <Field label="Time of birth" hint="Optional">
            <TextInput type="time" value={birthTime} onChange={(e) => setBirthTime(e.target.value)} />
          </Field>
        </div>

        <Field label="Sex" hint="Used for growth-chart lookups only. Optional.">
          <Select value={sex} onChange={(e) => setSex(e.target.value as typeof sex)}>
            <option value="">—</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="intersex">Intersex</option>
            <option value="unspecified">Prefer not to say</option>
          </Select>
        </Field>

        <div>
          <Kicker>Birth stats (optional)</Kicker>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <Field label="Weight (kg)">
              <TextInput type="number" inputMode="decimal" step="0.01" min={0} max={10} value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="3.4" />
            </Field>
            <Field label="Length (cm)">
              <TextInput type="number" inputMode="decimal" step="0.1" min={0} max={100} value={lengthCm} onChange={(e) => setLengthCm(e.target.value)} placeholder="50" />
            </Field>
            <Field label="Head (cm)">
              <TextInput type="number" inputMode="decimal" step="0.1" min={0} max={60} value={headCm} onChange={(e) => setHeadCm(e.target.value)} placeholder="35" />
            </Field>
          </div>
        </div>

        <Field label="Note (optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything you want to remember about the moment." />
        </Field>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>
        )}
      </div>
    </BottomSheet>
  )
}
